// App.js - Main Application Component
import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { AuthProvider, useAuth } from './contexts/AuthContext';
import Header from './components/Header/Header';
import Sidebar from './components/Sidebar/Sidebar';
import BottomNav from './components/BottomNav/BottomNav';
import Main from './components/Main/Main';
import Splashscreen from './components/Splashscreen';
import EnquiryModal from './components/EnquiryModal/EnquiryModal';
import FirstTimeAuthModal from './components/FirstTimeAuthModal/FirstTimeAuthModal';
import MexaLoader from './components/MexaLoader/MexaLoader';
import api from "./utils/apis";
import './App.css';
import { Toaster } from 'react-hot-toast';

// Lazy load route components
const Home = lazy(() => import('./views/Home/Home'));
const Personal = lazy(() => import('./views/Personal/Personal'));
const Address = lazy(() => import('./views/Address/Address'));
const KYC = lazy(() => import('./views/KYC/KYC'));
const Orders = lazy(() => import('./views/Orders/Orders'));
const OrderDetail = lazy(() => import('./views/Orders/OrderDetail'));
const Bank = lazy(() => import('./views/Bank/Bank'));
const Payments = lazy(() => import('./views/Payments/Payments'));
const Menu = lazy(() => import('./views/Menu/Menu'));
const Bonus = lazy(() => import('./views/Bonus/Bonus'));
const Transactions = lazy(() => import('./views/Transactions/Transactions'));
const Cart = lazy(() => import('./views/Cart/Cart'));
const Checkout = lazy(() => import('./views/Chackout/Checkouts'));
const ProductDetail = lazy(() => import('./views/ProductDetails/ProductDetails'));
const Login = lazy(() => import('./views/Login/Login'));
const ProductSearch = lazy(() => import('./views/ProductListings/ProductListings'));
const OrderSuccess = lazy(() => import('./views/OrderConfirmation/OrderSuccess'));
const OrderFailed = lazy(() => import('./views/OrderConfirmation/OrderFailed'));
const ResetPassword = lazy(() => import('./views/Login/ResetPassword'));
const Quotations = lazy(() => import('./views/Quotations/Quotations'));
const QuotationsDetailPage = lazy(() => import('./views/Quotations/QuatationsDetailPage'));
const SidebarMenu = lazy(() => import('./views/SidebarMenu/SidebarMenu'));
const PrivacyPolicy = lazy(() => import('./views/PrivacyPolicy/PrivacyPolicy'));
const DeleteAccount = lazy(() => import('./views/DeleteAccount/DeleteAccount'));


// Support Routes
const SupportList = lazy(() => import('./views/Support/SupportList'));
const CreateTicket = lazy(() => import('./views/Support/CreateTicket'));
const TicketDetail = lazy(() => import('./views/Support/TicketDetail'));
const EnquiryPage = lazy(() => import('./views/Enquiry/EnquiryPage'));

// Protected Route Component
const ProtectedRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <MexaLoader size="lg" text="Authenticating..." />
      </div>
    );
  }

  return isLoggedIn ? children : <Navigate to="/login" replace />;
};

// Public Route Component (redirect to home if already logged in)
const PublicRoute = ({ children }) => {
  const { isLoggedIn, loading } = useAuth();

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <MexaLoader size="lg" text="Loading..." />
      </div>
    );
  }

  return !isLoggedIn ? children : <Navigate to="/" replace />;
};

// Main App Content Component
const AppContent = () => {
  const { isLoggedIn, userData, logout, updateUserData } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  // FIX BUG-016: was using static window.innerWidth check (not reactive to resize)
  // Now updates correctly when the window is resized.
  const [isMobileView, setIsMobileView] = useState(window.innerWidth < 768);

  const [categoryItems, setCategories] = useState([]);
  const [brandItems, setbrands] = useState([]);
  const [productItems, setTrending] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [dataError, setDataError] = useState(null);
  const location = useLocation();

  // FIX BUG-016: add resize listener so isMobileView stays current
  useEffect(() => {
    const handleResize = () => setIsMobileView(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Fetch categories & ads together with improved error handling
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        setDataError(null);

        const [catRes, trendingRes, brandsRes] = await Promise.all([
          api.get("/categories"),
          api.get("/products/trendings"),
          api.get("/brands?categoryId=0&sub_categoryId=0"),
        ]);

        // Validate responses
        if (catRes?.data && trendingRes?.data && brandsRes?.data) {
          setCategories(catRes.data);
          setTrending(trendingRes.data);
          setbrands(brandsRes.data);
        } else {
          throw new Error('Invalid data structure from API');
        }
      } catch (err) {
        console.error("❌ Failed to fetch data", err);
        setDataError(err.message || 'Failed to load application data');
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []);

  const toggleSidebar = useCallback((open) => {
    setSidebarOpen(prev => typeof open === "boolean" ? open : !prev);
  }, []);

  const toggleMobileSearch = useCallback(() => {
    setMobileSearchOpen(prev => !prev);
  }, []);

  const closeSidebar = useCallback(() => {
    setSidebarOpen(false);
  }, []);

  const isAuthPage = location.pathname === '/login' || location.pathname === '/reset-password';
  const isEnquiryPage = location.pathname === '/enquiry';


  // Show error state if data loading failed
  if (dataError) {
    return (
      <div className="flex justify-center items-center min-h-screen bg-white">
        <div className="text-center max-w-md mx-4">
          <div className="text-red-500 text-6xl mb-4">⚠️</div>
          <h2 className="text-xl font-semibold text-gray-800 mb-2">Unable to Load SERVER</h2>
          <p className="text-gray-600 mb-4">{dataError}</p>
          <button
            onClick={() => window.location.reload()}
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }
  const layoutOpen = !isAuthPage;

  return (
    <div className="App bg-slate-50 text-dark min-h-screen">
      <Toaster position="top-right" />
      {!isAuthPage && (
        <Header
          isLoggedIn={isLoggedIn}
          toggleLoginState={logout}
          toggleSidebar={toggleSidebar}
          toggleMobileSearch={toggleMobileSearch}
          mobileSearchOpen={mobileSearchOpen}
          userData={userData}
          isMobileView={isMobileView}
        />
      )}


      <Main layoutOpen={layoutOpen}>
        <Suspense fallback={
          <div className="flex justify-center items-center min-h-[60vh]">
            <MexaLoader size="lg" />
          </div>
        }>
          <Routes>
            {/* Public Routes */}
            <Route
              path="/"
              element={
                <Home
                  categoryItems={categoryItems}
                  brandItems={brandItems}
                  productItems={productItems}
                  toggleMobileSearch={toggleMobileSearch}
                  loading={dataLoading}
                />
              }
            />
            <Route
              path="/menu"
              element={
                <Menu userData={userData} />
              }
            />
            <Route
              path="/product/:id"
              element={
                <ProductDetail />
              }
            />
            <Route
              path="/cart"
              element={
                <ProtectedRoute>
                  <Cart />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotations"
              element={
                <ProtectedRoute>
                  <Quotations />
                </ProtectedRoute>
              }
            />
            <Route
              path="/quotations/:id"
              element={
                <ProtectedRoute>
                  <QuotationsDetailPage />
                </ProtectedRoute>
              }
            />
            <Route
              path="/products"
              element={
                <ProductSearch categoryItems={categoryItems} />
              }
            />
            <Route
              path="/products/:categorySlug"
              element={
                <ProductSearch categoryItems={categoryItems} />
              }
            />
            <Route
              path="/products/:categorySlug/:subcategorySlug"
              element={
                <ProductSearch categoryItems={categoryItems} />
              }
            />
            <Route
              path="/brand/:brandSlug"
              element={
                <ProductSearch categoryItems={categoryItems} />
              }
            />

            {/* Public route - only login page is seen without login */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/reset-password"
              element={
                <PublicRoute>
                  <ResetPassword />
                </PublicRoute>
              }
            />

            {/* All other routes are protected */}
            <Route
              path="/personal"
              element={
                <ProtectedRoute>
                  <Personal userData={userData} onUpdateUser={updateUserData} />
                </ProtectedRoute>
              }
            />
            <Route
              path="/address"
              element={
                <ProtectedRoute>
                  <Address />
                </ProtectedRoute>
              }
            />
            <Route
              path="/kyc"
              element={
                <ProtectedRoute>
                  <KYC />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders"
              element={
                <ProtectedRoute>
                  <Orders />
                </ProtectedRoute>
              }
            />
            <Route
              path="/orders/:orderId"
              element={
                <ProtectedRoute>
                  <OrderDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bank"
              element={
                <ProtectedRoute>
                  <Bank />
                </ProtectedRoute>
              }
            />
            <Route
              path="/payments"
              element={
                <ProtectedRoute>
                  <Payments />
                </ProtectedRoute>
              }
            />
            <Route
              path="/bonus"
              element={
                <ProtectedRoute>
                  <Bonus />
                </ProtectedRoute>
              }
            />
            <Route
              path="/transactions"
              element={
                <ProtectedRoute>
                  <Transactions />
                </ProtectedRoute>
              }
            />
            <Route
              path="/checkout/:quotationId"
              element={
                <ProtectedRoute>
                  <Checkout />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-success"
              element={
                <ProtectedRoute>
                  <OrderSuccess />
                </ProtectedRoute>
              }
            />
            <Route
              path="/order-failed"
              element={
                <ProtectedRoute>
                  <OrderFailed />
                </ProtectedRoute>
              }
            />

            <Route
              path="/support"
              element={
                <ProtectedRoute>
                  <SupportList />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support/new"
              element={
                <ProtectedRoute>
                  <CreateTicket />
                </ProtectedRoute>
              }
            />
            <Route
              path="/support/:id"
              element={
                <ProtectedRoute>
                  <TicketDetail />
                </ProtectedRoute>
              }
            />
            <Route
              path="/enquiry"
              element={
                <EnquiryPage />
              }
            />

            <Route
              path="/privacy-policy"
              element={
                <PrivacyPolicy />
              }
            />
            <Route
              path="/delete-account"
              element={
                <DeleteAccount />
              }
            />

            {/* Fallback route - now public */}
            <Route
              path="*"
              element={
                <Home
                  categoryItems={categoryItems}
                  brandItems={brandItems}
                  productItems={productItems}
                  toggleMobileSearch={toggleMobileSearch}
                  loading={dataLoading}
                />
              }
            />
          </Routes>
        </Suspense>
      </Main>

      {/* Only show BottomNav if not on login/auth page */}
      {!isAuthPage && <BottomNav />}



      {/* Global Floating Enquiry Modal */}
      {!isEnquiryPage && <EnquiryModal />}

      {/* 45-Second First Time Visitor Auth Modal */}
      <FirstTimeAuthModal />

      <Splashscreen />
    </div>

  );

};

// Main App Component with AuthProvider
function App() {
  return (
    <AuthProvider>
      <AppContent />
    </AuthProvider>
  );
}

export default App;